import { QUOTA_CATEGORY } from '../constants';
import { Nullable } from '@ps/utils';

export interface QuotaRangeSetDto {
  from: number;
  to: number;
  units: number;
}

export type QuotaFlexibleDto = {
  enable: boolean;
  value: number;
};

export type QuotaCategoryDto =
  (typeof QUOTA_CATEGORY)[keyof typeof QUOTA_CATEGORY];

export interface QuotaCriterionDto {
  qualification_code: number;
  qualification_name: string;
  condition_codes: string[];
  condition_names: string[];
  range_sets: QuotaRangeSetDto[];
  original_maximum: number;
  original_minimum: number;
  original_percentage: number;
  original_required_count: number;
  original_number: number;
  original_flex: QuotaFlexibleDto;
  non_quota_targeted: boolean;
  // Only ever present on the 232 (child-age-gender) criterion — legacy's own sibling wire field
  // for the age unit (311 = years, 312 = months), since 232's condition_codes encode age+gender
  // combined and carry no unit of their own. See
  // ../child-age-gender/child-age-gender-quota.mapper.ts.
  child_age_unit?: number;
}

export interface IndividualQuotaAllocationDto {
  quantity: number;
  percentage: number;
  min: number;
  max: number;
}

export interface IndividualQuotaDto {
  allocation: IndividualQuotaAllocationDto;
  criteria: QuotaCriterionDto[];
}

export interface QuotaInFieldDto {
  total_complete: number;
  crtd_on: number;
}

/** A supplier entry on the quota list response. */
export interface QuotaSupplierDto {
  supplier_ref_id: string;
  name: string;
  current_target: number;
  currently_open: number;
  sup_currently_open: number;
  complete_fielded: number;
  complete_needed: number;
  buyer_valid_clks: number;
  bip: number;
  last_start_date: string;
  last_complete_date: string;
  is_pds_applied: boolean;
  fee: Nullable<number>;
}

/**
 * A supplier entry on the update-current-target response — targets only. Kept
 * separate from QuotaSupplierDto so the PATCH DTO can't claim fields that
 * response never returns.
 */
export interface QuotaSupplierTargetDto {
  current_target: number;
  complete_needed: number;
}

export interface SurveyQuotaDto {
  buyer_quota_id: string;
  ps_quota_id: string;
  quota_title?: string;
  criteria: QuotaCriterionDto[];
  locked: boolean;
  current_target: number;
  currently_open: number;
  achieved: number;
  maximum: number;
  percentage: number;
  quota_category: QuotaCategoryDto;
  group_key: string;
  in_field?: QuotaInFieldDto;
  suppliers?: QuotaSupplierDto[];
  bip?: number;
  required_count: number;
  buyer_valid_clks: number;
  // Not yet returned by the API — the AIDA alert badge (manage-quotas
  // parity) is built defensively against this landing later.
  aida_health_status?: 'RED' | 'YELLOW' | 'GREEN' | 'GREY';
  // Not yet returned by the API — the quota-info drawer (manage-quotas
  // parity) is built defensively against these landing later.
  last_start_date?: string;
  last_complete_date?: string;
  // Mirrors legacy Quotas.qbp: present only for QBP-enabled quotas.
  qbp?: {
    cpi: number;
    enabled: boolean;
    surveyId: number; // child survey id created by QBP for this quota — camelCase per the actual API response, unlike this DTO's other snake_case fields
  };
  // Not yet returned by the API — the quota-info drawer's Supplier Currently
  // Open row (manage-quotas parity) is built defensively against this landing
  // later. Distinct from suppliers[].sup_currently_open, which is per-supplier.
  sup_currently_open?: number;
  individual_quotas?: IndividualQuotaDto[];
}

export interface QuotaUpdateQuantitiesDto {
  current_target: number;
  currently_open: number;
  maximum: number;
  achieved: number;
  percentage: number;
}

/**
 * Shape returned by the update-current-target PATCH. Note `suppliers` is a
 * map keyed by supplier ref id here (not an array as in the list response)
 * and the target lives under `quantities`.
 */
export interface QuotaUpdateResultDto {
  quota_id: string;
  locked: boolean;
  suppliers: Record<string, QuotaSupplierTargetDto>;
  quantities: QuotaUpdateQuantitiesDto;
}

export interface QuotaPatchOperationDto {
  op: string;
  path: string;
  value: boolean | string | number;
  updateSupplier?: boolean;
}

export type SupplierQuotaPatchField = 'currentTarget' | 'currentlyOpen';

export const SUPPLIER_QUOTA_PATCH_PATH: Record<
  SupplierQuotaPatchField,
  string
> = {
  currentTarget: '/current_target',
  currentlyOpen: '/current_open',
};

export type QuotaPayloadDto = Pick<
  SurveyQuotaDto,
  | 'buyer_quota_id'
  | 'quota_title'
  | 'group_key'
  | 'maximum'
  | 'percentage'
  | 'quota_category'
  | 'criteria'
  | 'individual_quotas'
> & {
  required_count: number;
  flexible: QuotaFlexibleDto;
  minimum: number;
};

export type SurveyQuotaEntryDto = QuotaPayloadDto &
  Pick<SurveyQuotaDto, 'current_target' | 'achieved'>;

export interface QuotaPricingRecoPayloadDto {
  survey_id: number;
  country: string;
  completes_remaining: number;
  IR: number;
  LOI: number;
  field_time: number;
  statistics: true;
  age_grp?: string;
  gender?: string;
  race?: string;
  hispanic_origin?: string;
}

export type QualificationPayloadKey =
  | 'gender'
  | 'age_grp'
  | 'race'
  | 'hispanic_origin';
